// Composant générique de saisie de trésorerie (Banque ou Caisse).
// Entrées / sorties d'argent, contrepartie tiers OU catégorie, solde courant.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SelecteurTiers from "@/components/SelecteurTiers";
import SelecteurCompte from "@/components/SelecteurCompte";
import DatePicker from "@/components/DatePicker";
import JournalEcritures from "@/components/JournalEcritures";
import ApercuEcritures from "@/components/ApercuEcritures";
import { Loader2, ArrowDownCircle, ArrowUpCircle, Lock, AlertTriangle, ClipboardCheck, FileText } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const fmt = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} XPF`;
const aujourdHui = () => new Date().toISOString().slice(0, 10);

export default function SaisieTresorerie({ config }) {
  const { journal, titre, sousTitre, icone: Icone, moyensPaiement, pageTestId, arrete,
    journalCode, libelleJournal, compteTresorerie } = config;

  const [sens, setSens] = useState("entree");
  const [dateMouvement, setDateMouvement] = useState(aujourdHui());
  const [montant, setMontant] = useState("");
  const [description, setDescription] = useState("");
  const [moyen, setMoyen] = useState(moyensPaiement[0] || "");
  const [reference, setReference] = useState("");
  const [contrepartieType, setContrepartieType] = useState("tiers");
  const [tiers, setTiers] = useState(null);
  const [compte, setCompte] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [mouvements, setMouvements] = useState([]);
  const [solde, setSolde] = useState(0);
  const [resetSeq, setResetSeq] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  // Aperçu des écritures : encaissement => débit trésorerie / crédit contrepartie ;
  // décaissement => débit contrepartie / crédit trésorerie.
  const apercuLignes = (() => {
    const m = Math.round(parseFloat(montant) || 0);
    const cpteTreso = compteTresorerie || (journal === "banque" ? "512000" : "530000");
    const libTreso = journal === "banque" ? "Banque" : "Caisse";
    const cpteCp = contrepartieType === "tiers" ? (tiers?.compte || "Tiers") : (compte?.numero || "Compte");
    const libCp = contrepartieType === "tiers" ? (tiers?.nom || "Tiers") : (compte?.libelle || "Catégorie");
    const entree = sens === "entree";
    return [
      { compte: cpteTreso, libelle: libTreso, debit: entree ? m : 0, credit: entree ? 0 : m },
      { compte: cpteCp, libelle: libCp, debit: entree ? 0 : m, credit: entree ? m : 0 },
    ];
  })();

  // --- État spécifique à l'arrêté de caisse ---
  const [arretes, setArretes] = useState([]);
  const [arreteManquant, setArreteManquant] = useState(null);
  const [dialogArrete, setDialogArrete] = useState(false);
  const [soldeTheorique, setSoldeTheorique] = useState(0);
  const [montantReel, setMontantReel] = useState("");
  const [confirmEcart, setConfirmEcart] = useState(false);
  const [validation, setValidation] = useState(false);

  const charger = useCallback(async () => {
    try {
      const requetes = [
        api.get("/tresorerie/mouvements", { params: { journal } }),
        api.get("/tresorerie/solde", { params: { journal } }),
      ];
      if (arrete) {
        requetes.push(api.get("/caisse/arretes"));
        requetes.push(api.get("/caisse/arrete-manquant"));
      }
      const res = await Promise.all(requetes);
      setMouvements(res[0].data || []);
      setSolde(res[1].data?.solde || 0);
      if (arrete) {
        setArretes(res[2].data || []);
        setArreteManquant(res[3].data || null);
      }
    } catch {
      setMouvements([]);
    }
  }, [journal, arrete]);

  useEffect(() => { charger(); }, [charger]);

  const reinitialiser = () => {
    setSens("entree");
    setDateMouvement(aujourdHui());
    setMontant("");
    setDescription("");
    setReference("");
    setMoyen(moyensPaiement[0] || "");
    setContrepartieType("tiers");
    setTiers(null);
    setCompte(null);
    setResetSeq((s) => s + 1);
  };

  const enregistrer = async () => {
    if (!(parseFloat(montant) > 0)) {
      toast.error("Veuillez saisir un montant valide.");
      return;
    }
    if (!description.trim()) {
      toast.error("Veuillez saisir une description.");
      return;
    }
    if (contrepartieType === "tiers" && !tiers) {
      toast.error("Veuillez sélectionner un tiers.");
      return;
    }
    if (contrepartieType === "compte" && !compte) {
      toast.error("Veuillez sélectionner une catégorie.");
      return;
    }
    setEnregistrement(true);
    try {
      await api.post("/tresorerie/mouvements", {
        journal, sens,
        date_mouvement: dateMouvement,
        montant: Math.round(parseFloat(montant)),
        description,
        moyen_paiement: moyen || null,
        reference: reference || null,
        contrepartie_type: contrepartieType,
        tiers_id: contrepartieType === "tiers" ? tiers.id : null,
        compte: contrepartieType === "compte" ? compte.numero : null,
        libelle_compte: contrepartieType === "compte" ? compte.libelle : null,
      });
      toast.success("Mouvement enregistré avec succès.");
      reinitialiser();
      charger();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setEnregistrement(false);
    }
  };

  // --- Arrêté de caisse ---
  const ouvrirArrete = async () => {
    setConfirmEcart(false);
    setMontantReel("");
    try {
      const { data } = await api.get("/caisse/solde-theorique", { params: { date: aujourdHui() } });
      setSoldeTheorique(data.solde_theorique || 0);
    } catch {
      setSoldeTheorique(0);
    }
    setDialogArrete(true);
  };

  const reelNum = montantReel === "" ? null : Math.round(parseFloat(montantReel) || 0);
  const ecartArrete = reelNum === null ? null : reelNum - Math.round(soldeTheorique);
  const equilibre = ecartArrete === 0;

  const validerArrete = async () => {
    if (reelNum === null) {
      toast.error("Veuillez saisir le montant réel en caisse.");
      return;
    }
    if (!equilibre && !confirmEcart) {
      setConfirmEcart(true); // premier clic : demande de confirmation
      return;
    }
    setValidation(true);
    try {
      await api.post("/caisse/arretes", { date: aujourdHui(), solde_reel: reelNum });
      toast.success("Arrêté de caisse validé.");
      setDialogArrete(false);
      charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setValidation(false);
    }
  };

  const telechargerArretePdf = async (a) => {
    try {
      const res = await api.get(`/caisse/arretes/${a.id}/pdf`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `arrete_caisse_${a.date}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Le téléchargement du reçu a échoué.");
    }
  };

  // Historique combiné (mouvements + arrêtés) trié par date décroissante
  const historique = [
    ...mouvements.map((m) => ({ kind: "mouvement", date: m.date_mouvement, data: m })),
    ...arretes.map((a) => ({ kind: "arrete", date: a.date, data: a })),
  ].sort((x, y) => (y.date || "").localeCompare(x.date || "")).slice(0, 12);

  // La journée du jour est-elle déjà arrêtée ? (saisie alors verrouillée)
  const journeeArretee = arrete && arretes.some((a) => a.date === aujourdHui());

  return (
    <div className="fenua-fade-up" data-testid={pageTestId}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <Icone className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">{titre}</h1>
            <p className="text-sm text-[#1D3D3D]/70">{sousTitre}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {arrete && (
            <Button data-testid="bouton-arrete-caisse" onClick={ouvrirArrete}
              variant="secondary"
              className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/30 font-semibold">
              <ClipboardCheck className="h-4 w-4 mr-1" /> Arrêté de caisse
            </Button>
          )}
          <div className="bg-[#1D3D3D] text-white rounded-lg px-5 py-3 text-right">
            <p className="text-xs uppercase tracking-wide text-white/70">Solde courant</p>
            <p data-testid="solde-tresorerie" className="text-xl font-black">{fmt(solde)}</p>
          </div>
        </div>
      </div>

      {/* Alerte arrêté manquant */}
      {arrete && arreteManquant?.manquant && (
        <div data-testid="alerte-arrete-manquant"
          className="mt-4 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Vous avez des mouvements de caisse non arrêtés depuis le {arreteManquant.depuis}. Pensez à faire votre arrêté de caisse.</span>
        </div>
      )}

      {journalCode && (
        <div className="mt-6">
          <JournalEcritures code={journalCode} libelleJournal={libelleJournal} refreshKey={refreshKey} />
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Formulaire */}
        <div className="xl:col-span-2 bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6 space-y-5">
          {/* Sens */}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="sens-entree" onClick={() => setSens("entree")}
              className={`flex items-center justify-center gap-2 h-11 rounded-md text-sm font-semibold transition-colors ${
                sens === "entree" ? "bg-[#1D3D3D] text-white" : "bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20"
              }`}>
              <ArrowDownCircle className="h-4 w-4" /> Encaissement
            </button>
            <button type="button" data-testid="sens-sortie" onClick={() => setSens("sortie")}
              className={`flex items-center justify-center gap-2 h-11 rounded-md text-sm font-semibold transition-colors ${
                sens === "sortie" ? "bg-[#1D3D3D] text-white" : "bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20"
              }`}>
              <ArrowUpCircle className="h-4 w-4" /> Décaissement
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Date</Label>
              <DatePicker testId="mouvement-date" value={dateMouvement} onChange={setDateMouvement} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Montant</Label>
              <Input type="number" step="1" min="1" inputMode="numeric" data-testid="mouvement-montant"
                value={montant} onChange={(e) => setMontant(e.target.value)} placeholder="0"
                className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Moyen de paiement</Label>
              <Select value={moyen} onValueChange={setMoyen}>
                <SelectTrigger data-testid="mouvement-moyen" className="bg-white border-[#4A8A8A]/30">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {moyensPaiement.map((mp) => (<SelectItem key={mp} value={mp}>{mp}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Référence (facultatif)</Label>
              <Input data-testid="mouvement-reference" value={reference}
                onChange={(e) => setReference(e.target.value)} placeholder="N° de chèque, etc."
                className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[#1D3D3D] font-semibold text-sm">Description</Label>
            <Input data-testid="mouvement-description" value={description}
              onChange={(e) => setDescription(e.target.value)} placeholder="Objet du mouvement"
              className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
          </div>

          {/* Contrepartie */}
          <div className="space-y-2">
            <Label className="text-[#1D3D3D] font-semibold text-sm">Origine / destination</Label>
            <div className="flex gap-2">
              <button type="button" data-testid="contrepartie-tiers"
                onClick={() => { setContrepartieType("tiers"); setCompte(null); }}
                className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors ${
                  contrepartieType === "tiers" ? "bg-[#2D5A5A] text-white" : "bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20"
                }`}>
                Un tiers
              </button>
              <button type="button" data-testid="contrepartie-compte"
                onClick={() => { setContrepartieType("compte"); setTiers(null); }}
                className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors ${
                  contrepartieType === "compte" ? "bg-[#2D5A5A] text-white" : "bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20"
                }`}>
                Une catégorie
              </button>
            </div>
            {contrepartieType === "tiers" ? (
              <SelecteurTiers key={`t-${resetSeq}`} onSelect={setTiers} testId="mouvement-selecteur-tiers" />
            ) : (
              <SelecteurCompte key={`c-${resetSeq}`} testId="mouvement-selecteur-compte"
                onSelect={setCompte} placeholder="Choisir une catégorie" />
            )}
          </div>
        </div>

        {/* Récap + historique */}
        <div className="space-y-6">
          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6">
            <h3 className="font-bold text-[#1D3D3D] mb-3">Validation</h3>
            <p className="text-sm text-[#1D3D3D]/70">
              {sens === "entree" ? "Encaissement" : "Décaissement"} de <span className="font-semibold text-[#1D3D3D]">{fmt(parseFloat(montant) || 0)}</span>
            </p>
            {journeeArretee && (
              <p data-testid="journee-verrouillee" className="mt-2 text-xs text-amber-700 flex items-center gap-1">
                <Lock className="h-3 w-3" /> La journée du jour est arrêtée : saisie verrouillée.
              </p>
            )}
            <Button data-testid="bouton-enregistrer-mouvement" onClick={enregistrer} disabled={enregistrement || journeeArretee}
              className="w-full mt-4 bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-semibold h-11">
              {enregistrement ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer le mouvement"}
            </Button>
          </div>

          <ApercuEcritures lignes={apercuLignes} />

          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6">
            <h3 className="font-bold text-[#1D3D3D] mb-4">Derniers mouvements</h3>
            {historique.length === 0 ? (
              <p className="text-sm text-[#1D3D3D]/60">Aucun mouvement pour le moment.</p>
            ) : (
              <div className="space-y-2" data-testid="liste-mouvements">
                {historique.map((item) => {
                  if (item.kind === "arrete") {
                    const a = item.data;
                    return (
                      <div key={`a-${a.id}`} data-testid={`arrete-item-${a.id}`}
                        className="flex items-center justify-between text-sm py-2 px-2 rounded-md bg-[#E8F4F4] border border-[#4A8A8A]/20">
                        <span className="flex items-center gap-2 text-[#1D3D3D] font-medium min-w-0">
                          <Lock className="h-4 w-4 text-[#4A8A8A] shrink-0" />
                          <span className="truncate">
                            Arrêté de caisse — {a.date} — {a.statut === "equilibre" ? "Équilibré" : `Écart : ${fmt(a.ecart)}`}
                          </span>
                        </span>
                        <Button variant="ghost" size="sm" data-testid={`arrete-pdf-${a.id}`}
                          onClick={() => telechargerArretePdf(a)}
                          className="shrink-0 ml-2 h-7 px-2 text-[#1D3D3D] hover:bg-[#4A8A8A]/20">
                          <FileText className="h-4 w-4 mr-1" /> Reçu PDF
                        </Button>
                      </div>
                    );
                  }
                  const m = item.data;
                  return (
                    <div key={`m-${m.id}`} data-testid={`mouvement-item-${m.id}`} className="flex items-center justify-between text-sm py-2 border-b border-[#4A8A8A]/10 last:border-0">
                      <div className="min-w-0">
                        <p className="text-[#1D3D3D] font-medium truncate flex items-center gap-1">
                          {m.verrouille && <Lock className="h-3 w-3 text-[#4A8A8A] shrink-0" />}
                          {m.description}
                        </p>
                        <p className="text-[#1D3D3D]/60 text-xs truncate">{m.contrepartie_libelle} · {m.date_mouvement}</p>
                      </div>
                      <span className={`font-semibold shrink-0 ml-2 ${m.sens === "entree" ? "text-emerald-600" : "text-red-600"}`}>
                        {m.sens === "entree" ? "+" : "−"}{fmt(m.montant)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Écran dédié : Arrêté de caisse */}
      {arrete && (
        <Dialog open={dialogArrete} onOpenChange={setDialogArrete}>
          <DialogContent className="bg-white" data-testid="dialog-arrete">
            <DialogHeader>
              <DialogTitle className="text-[#1D3D3D] flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-[#4A8A8A]" /> Arrêté de caisse
              </DialogTitle>
              <DialogDescription className="text-[#1D3D3D]/70">
                Comptez physiquement votre caisse, saisissez le montant constaté puis validez. L'arrêté est définitif.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-[#1D3D3D]/70">Date du jour</span>
                <span data-testid="arrete-date" className="font-semibold text-[#1D3D3D]">{aujourdHui()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#1D3D3D]/70">Solde théorique</span>
                <span data-testid="arrete-solde-theorique" className="font-semibold text-[#1D3D3D]">{fmt(soldeTheorique)}</span>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Montant réel en caisse (XPF)</Label>
                <Input type="number" step="1" min="0" inputMode="numeric" data-testid="arrete-montant-reel"
                  value={montantReel} onChange={(e) => { setMontantReel(e.target.value); setConfirmEcart(false); }}
                  placeholder="Comptez votre caisse et saisissez le montant"
                  className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
              </div>

              {reelNum !== null && (
                <div className="rounded-md border border-[#4A8A8A]/20 p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#1D3D3D]/70">Écart</span>
                    <span data-testid="arrete-ecart" className={`font-bold ${equilibre ? "text-emerald-600" : "text-red-600"}`}>
                      {ecartArrete > 0 ? "+" : ""}{fmt(ecartArrete)}
                    </span>
                  </div>
                  {equilibre ? (
                    <p data-testid="arrete-message" className="mt-2 text-sm text-emerald-700">Caisse équilibrée — vous pouvez valider</p>
                  ) : (
                    <p data-testid="arrete-message" className="mt-2 text-sm text-red-600">
                      Écart constaté de {fmt(ecartArrete)}. Vérifiez vos saisies avant de valider.
                    </p>
                  )}
                </div>
              )}

              {confirmEcart && !equilibre && (
                <div data-testid="arrete-confirmation" className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  Êtes-vous sûr ? Cet arrêté sera définitif et ne pourra pas être modifié.
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              {!equilibre && reelNum !== null && (
                <Button variant="ghost" data-testid="arrete-corriger" onClick={() => setDialogArrete(false)}>
                  Corriger mes saisies
                </Button>
              )}
              <Button
                data-testid="arrete-valider"
                onClick={validerArrete}
                disabled={validation || reelNum === null}
                className={`font-semibold text-white ${equilibre ? "bg-[#1D3D3D] hover:bg-[#2D5A5A]" : "bg-red-600 hover:bg-red-700"}`}
              >
                {validation ? <Loader2 className="h-4 w-4 animate-spin" />
                  : equilibre ? "Valider l'arrêté"
                  : confirmEcart ? "Confirmer définitivement" : "Valider malgré l'écart"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
